import type { Product, ProductCreateInput } from '@/lib/api/products'

export const DEFAULT_CATEGORY = 'Sin Categoría'

export interface SpreadsheetProductRow {
  clientId: string
  id: string
  sku: string
  name: string
  description: string
  price: string
  currency: string
  stock: string
  category: string
  keywords: string
  commercialRole: string
  imageUrl?: string | null
  isDirty?: boolean
  isNew?: boolean
}

/**
 * Convierte un producto del backend a una fila de spreadsheet con valores formateados
 */
export function mapProductToSpreadsheetRow(product: Product): SpreadsheetProductRow {
  const rawRole = (product.commercialRole || '').trim().toLowerCase()
  const commercialRole =
    rawRole === 'core' || rawRole === 'base' || rawRole === 'principal'
      ? 'main'
      : product.commercialRole || ''

  return {
    clientId: `prod-${product.id}`,
    id: product.id,
    sku: product.sku || '',
    name: product.name || '',
    description: product.description || '',
    price: (product.priceCents / 100).toFixed(2),
    currency: product.currency || 'USD',
    stock: (product.stock ?? 0).toString(),
    category: product.category || DEFAULT_CATEGORY,
    keywords: product.keywords || '',
    commercialRole,
    imageUrl: product.imageUrl || null,
    isDirty: false,
    isNew: false,
  }
}

/**
 * Crea una nueva fila en blanco para añadir rápidamente un producto al spreadsheet
 */
export function createEmptySpreadsheetRow(
  defaultCategory: string = DEFAULT_CATEGORY,
  defaultStock: number = 0
): SpreadsheetProductRow {
  const tempId = `new-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`
  return {
    clientId: tempId,
    id: '',
    sku: '',
    name: '',
    description: '',
    price: '0.00',
    currency: 'USD',
    stock: defaultStock.toString(),
    category: defaultCategory || DEFAULT_CATEGORY,
    keywords: '',
    commercialRole: '',
    imageUrl: null,
    isDirty: true,
    isNew: true,
  }
}

/**
 * Actualiza un campo de una fila de forma inmutable y marca la fila como modificada (isDirty)
 */
export function updateSpreadsheetRowField(
  rows: SpreadsheetProductRow[],
  clientId: string,
  field: keyof SpreadsheetProductRow,
  value: any
): SpreadsheetProductRow[] {
  return rows.map((row) => {
    if (row.clientId !== clientId) return row
    return {
      ...row,
      [field]: value,
      isDirty: true,
    }
  })
}

/**
 * Elimina una fila del spreadsheet de forma inmutable
 */
export function removeSpreadsheetRow(
  rows: SpreadsheetProductRow[],
  clientId: string
): SpreadsheetProductRow[] {
  return rows.filter((row) => row.clientId !== clientId)
}

/**
 * Filtra las filas según búsqueda y categoría sin alterar los datos originales
 */
export function filterSpreadsheetRows(
  rows: SpreadsheetProductRow[],
  searchQuery: string,
  categoryFilter: string,
  defaultCategory: string = DEFAULT_CATEGORY
): SpreadsheetProductRow[] {
  const query = searchQuery.trim().toLowerCase()
  return rows.filter((row) => {
    const matchesSearch =
      !query ||
      row.name.toLowerCase().includes(query) ||
      row.sku.toLowerCase().includes(query) ||
      (row.category || '').toLowerCase().includes(query)

    const rowCategory = row.category || defaultCategory
    const matchesCategory = !categoryFilter || rowCategory === categoryFilter

    return matchesSearch && matchesCategory
  })
}

/**
 * Devuelve únicamente las filas que han sido modificadas o creadas
 */
export function getDirtySpreadsheetRows(
  rows: SpreadsheetProductRow[]
): SpreadsheetProductRow[] {
  return rows.filter((row) => Boolean(row.isDirty || row.isNew))
}

export interface ValidationResult {
  isValid: boolean
  error?: string
  productData?: ProductCreateInput
}

/**
 * Valida y formatea una fila del spreadsheet para su guardado en el backend
 */
export function validateSpreadsheetRow(row: SpreadsheetProductRow): ValidationResult {
  const name = row.name.trim()
  if (!name) {
    return {
      isValid: false,
      error: 'El nombre del producto es obligatorio.',
    }
  }

  let sku = row.sku.trim()
  if (!sku) {
    if (row.isNew) {
      // Auto-generar SKU si es nuevo y el usuario lo dejó vacío
      sku = `SKU-${Date.now().toString().slice(-6)}`
    } else {
      return {
        isValid: false,
        error: `El producto "${name}" requiere un código SKU.`,
      }
    }
  }

  const rawPrice = parseFloat(row.price)
  const price = isNaN(rawPrice) || rawPrice < 0 ? 0 : rawPrice

  const rawStock = parseInt(row.stock, 10)
  const stock = isNaN(rawStock) || rawStock < 0 ? 0 : rawStock

  const rawRole = (row.commercialRole || '').trim().toLowerCase()
  const commercialRole =
    rawRole === 'core' || rawRole === 'base' || rawRole === 'principal'
      ? 'main'
      : row.commercialRole.trim() || null

  const productData: ProductCreateInput = {
    sku,
    name,
    description: row.description.trim() || undefined,
    priceCents: Math.round(price * 100),
    currency: row.currency || 'USD',
    stock,
    category: row.category || DEFAULT_CATEGORY,
    keywords: row.keywords.trim() || undefined,
    commercialRole,
  }

  return {
    isValid: true,
    productData,
  }
}
